import {
  Badge,
  Box,
  Button,
  Center,
  Flex,
  Grid,
  Icon,
  Spinner,
  Text
} from "@chakra-ui/react";
import {
  FiBarChart,
  FiFileText,
  FiFilter,
  FiGrid,
  FiLock,
  FiRefreshCw,
  FiTarget,
  FiZap,
  FiEdit2
} from "react-icons/fi";
import { IconType } from "react-icons";
import { useCallback, useEffect, useState } from "react";
import {
  ModelosPipelineEndpoints,
  PipelineModeloDTO
} from "components/modelos-pipeline-components/utils";
import { BLOCKS_BY_OPERATION } from "../node-catalog";
import { StatesMachine } from "../classes/state-machine/state-machine";

// ─── Constants ────────────────────────────────────────────────────────────────

const ICON_MAP: Record<string, IconType> = {
  refresh: FiRefreshCw,
  filter: FiFilter,
  target: FiTarget,
  file: FiFileText,
  lock: FiLock,
  grid: FiGrid,
  chart: FiBarChart,
  zap: FiZap
};

const CONVENIO_OPTIONS = [
  { label: "Todos", value: "" },
  { label: "INSS", value: "INSS", color: "#2F80ED" },
  { label: "GOV", value: "GOV", color: "#4DA8A1" },
  { label: "FGTS", value: "FGTS", color: "#E24A4A" },
  { label: "CLT", value: "CLT", color: "#C9994A" }
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getIcon(key?: string): IconType {
  return (key ? ICON_MAP[key] : undefined) ?? FiZap;
}

type BlockStep = { label: string; accentColor: string; softColor: string };

function getBlockSteps(sequenciaBloco?: string): BlockStep[] {
  if (!sequenciaBloco) return [];
  try {
    const parsed = JSON.parse(sequenciaBloco);
    const machine = new StatesMachine(parsed);
    return machine
      .getVisibleStates()
      .filter((s) => !["START", "END"].includes(s.operation))
      .map((s) => {
        const def = BLOCKS_BY_OPERATION[s.operation];
        console.log("Def for", s.operation, def);
        return {
          label: def?.label ?? s.operation,
          accentColor: def?.accentColor ?? "#94A3B8",
          softColor: def?.softColor ?? "#F1F5F9"
        };
      });
  } catch {
    return [];
  }
}

// ─── Template Card ────────────────────────────────────────────────────────────

function TemplateCard({
  modelo,
  isRecommended,
  onApply
}: {
  modelo: PipelineModeloDTO;
  isRecommended: boolean;
  onApply: () => void;
}) {
  const IconComp = getIcon(modelo.icone);
  const accentColor = modelo.cor ?? "#2F80ED";
  const softColor = `${accentColor}18`;
  const steps = getBlockSteps(modelo.sequenciaBloco);
  const convenios = modelo.convenio
    ? modelo.convenio
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : [];

  return (
    <Flex
      direction="column"
      bg="white"
      border="1.5px solid"
      borderColor="#E2E8F0"
      borderLeft="3px solid"
      borderLeftColor={accentColor}
      borderRadius="10px"
      overflow="hidden"
      cursor="pointer"
      w="300px"
      onClick={onApply}
      _hover={{
        borderColor: accentColor,
        borderLeftColor: accentColor,
        boxShadow: "md"
      }}
      transition="all 0.15s"
      position="relative"
    >
      {isRecommended && (
        <Badge
          position="absolute"
          top="10px"
          right="10px"
          px="8px"
          py="3px"
          borderRadius="6px"
          fontSize="10px"
          fontWeight="700"
          bg="#3ABB78"
          color="white"
          textTransform="none"
          letterSpacing="0.2px"
        >
          Recomendado
        </Badge>
      )}

      <Flex direction="column" p="16px" gap="12px" flex={1}>
        {/* Icon + name */}
        <Flex align="flex-start" gap="10px" pr={isRecommended ? "80px" : "0"}>
          <Box
            w="36px"
            h="36px"
            borderRadius="8px"
            bg={softColor}
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
          >
            <Icon as={IconComp} color={accentColor} w="17px" h="17px" />
          </Box>
          <Box minW={0}>
            <Text
              fontSize="14px"
              fontWeight="700"
              color="#2D3748"
              noOfLines={1}
            >
              {modelo.nome}
            </Text>
            {modelo.descricaoCurta && (
              <Text fontSize="11px" color="#718096" noOfLines={2} mt="2px">
                {modelo.descricaoCurta}
              </Text>
            )}
          </Box>
        </Flex>

        {/* Block chain pills */}
        {steps.length > 0 && (
          <Flex align="center" flexWrap="wrap" gap="4px">
            {steps.map((step, i) => (
              <Flex key={i} align="center" gap="4px">
                <Badge
                  px="8px"
                  py="3px"
                  borderRadius="5px"
                  fontSize="11px"
                  fontWeight="600"
                  bg={step.softColor}
                  color={step.accentColor}
                  textTransform="none"
                >
                  {step.label}
                </Badge>
                {i < steps.length - 1 && (
                  <Text fontSize="11px" color="#94A3B8" lineHeight="1">
                    →
                  </Text>
                )}
              </Flex>
            ))}
          </Flex>
        )}
      </Flex>

      {/* Footer */}
      <Flex
        px="16px"
        py="10px"
        borderTop="1px solid #EDF2F7"
        align="center"
        justify="space-between"
      >
        <Flex gap="4px" flexWrap="wrap">
          {convenios.map((c) => {
            const opt = CONVENIO_OPTIONS.find((o) => o.value === c);
            return (
              <Badge
                key={c}
                px="7px"
                py="2px"
                borderRadius="5px"
                fontSize="11px"
                fontWeight="700"
                bg={opt?.color ? `${opt.color}15` : "#EDF2F7"}
                color={opt?.color ?? "#718096"}
                textTransform="none"
              >
                {c}
              </Badge>
            );
          })}
        </Flex>
        {steps.length > 0 && (
          <Text fontSize="11px" color="#94A3B8" whiteSpace="nowrap">
            {steps.length} {steps.length === 1 ? "bloco" : "blocos"}
          </Text>
        )}
      </Flex>
    </Flex>
  );
}

// ─── Gallery Overlay ──────────────────────────────────────────────────────────

export function PipelineTemplateGallery({
  mailingConvenio,
  mailingTipoOperacao,
  onApply,
  onDismiss
}: {
  mailingConvenio?: string;
  mailingTipoOperacao?: string;
  onApply: (modelo: PipelineModeloDTO) => void;
  onDismiss: () => void;
}) {
  const [modelos, setModelos] = useState<PipelineModeloDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const isMailingResolved =
    mailingConvenio !== undefined && mailingTipoOperacao !== undefined;

  const load = useCallback(async () => {
    if (!isMailingResolved) {
      return;
    }

    setIsLoading(true);
    try {
      const { data } = await ModelosPipelineEndpoints.list(
        mailingConvenio,
        mailingTipoOperacao
      );
      const activeModelos = data.filter((m) => m.status === "ATIVO");
      setModelos(activeModelos);

      if (activeModelos.length === 0) {
        onDismiss();
      }
    } catch {
      // Fallback to manual build if gallery cannot be loaded.
      onDismiss();
    } finally {
      setIsLoading(false);
    }
  }, [isMailingResolved, mailingConvenio, mailingTipoOperacao, onDismiss]);

  useEffect(() => {
    if (!isMailingResolved) {
      return;
    }

    load();
  }, [isMailingResolved, load]);

  if (!isMailingResolved) {
    return null;
  }

  if (isLoading) {
    return (
      <Flex
        position="absolute"
        inset={0}
        zIndex={12}
        bg="rgba(255, 255, 255, 0.6)"
        backdropFilter="blur(8px)"
        align="center"
        justify="center"
      >
        <Spinner color="primary.400" size="md" />
      </Flex>
    );
  }

  if (!isLoading && modelos.length === 0) {
    return null;
  }

  return (
    <Flex
      position="absolute"
      inset={0}
      zIndex={12}
      bg="rgba(255, 255, 255, 0.6)"
      backdropFilter="blur(8px)"
      direction="column"
      overflow="auto"
    >
      {/* Header */}
      <Flex
        direction="column"
        align="center"
        px="32px"
        pt="40px"
        pb="0"
        flexShrink={0}
      >
        <Text
          fontSize="22px"
          fontWeight="700"
          color="#2D3748"
          textAlign="center"
        >
          Escolha um modelo para começar
        </Text>
        <Text fontSize="13px" color="#718096" mt="6px" textAlign="center">
          Modelos recomendados usam o convênio do mailing e desenham
          automaticamente o fluxograma.
        </Text>
      </Flex>

      {/* Content */}
      <Center px="32px" py="24px">
        {modelos.length === 0 ? (
          <Flex
            direction="column"
            align="center"
            justify="center"
            h="200px"
            gap="8px"
          >
            <Text fontSize="15px" color="#96A2B3" fontWeight="500">
              Nenhum modelo disponível.
            </Text>
          </Flex>
        ) : (
          <Grid gridTemplateColumns="1fr 1fr 1fr" gap="16px">
            {modelos.map((m) => {
              // const isRecommended =
              //   !!mailingConvenio &&
              //   !!m.convenio
              //     ?.split(",")
              //     .map((s) => s.trim())
              //     .includes(mailingConvenio);
              return (
                <TemplateCard
                  key={m.id}
                  modelo={m}
                  isRecommended={false}
                  onApply={() => onApply(m)}
                />
              );
            })}
          </Grid>
        )}
      </Center>

      {/* Bottom CTA */}
      {mailingConvenio === "INSS" &&
      mailingTipoOperacao === "REFIN_CARTEIRA" ? (
        <></>
      ) : (
        <Flex justify="center" pb="32px" flexShrink={0}>
          <Button
            variant="outline"
            size="md"
            leftIcon={<Icon as={FiEdit2} w="14px" h="14px" />}
            onClick={onDismiss}
            color="#718096"
            borderColor="#CBD5E0"
            borderStyle="dashed"
            borderWidth="1.5px"
            borderRadius="8px"
            _hover={{ bg: "white", borderColor: "#94A3B8", color: "#4A5568" }}
          >
            Começar do zero
          </Button>
        </Flex>
      )}
    </Flex>
  );
}
